import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils/cn.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  glow?: boolean;
  fullWidth?: boolean;
  asChild?: boolean;
}

const BASE =
  'inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap select-none transition-all duration-200 ease-out outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)] focus-visible:ring-[var(--primary)]';

const SIZE_MAP: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-[13px] rounded-lg',
  md: 'h-11 px-5 text-[14px] rounded-lg',
  lg: 'h-12 px-6 text-[15px] rounded-xl',
};

const VARIANT_MAP: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--primary)] text-[var(--primary-on)] hover:bg-[var(--primary-hover)] active:bg-[var(--primary-press)]',
  secondary:
    'bg-transparent text-[var(--text-primary)] border border-[var(--border-strong)] hover:bg-[var(--bg-elevated)]',
  ghost:
    'bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)]',
  danger:
    'bg-[var(--danger)] text-white hover:opacity-90',
  outline:
    'bg-transparent text-[var(--primary)] border border-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--primary-on)]',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyProps = any;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      glow = false,
      fullWidth = false,
      disabled = false,
      className,
      asChild = false,
      children,
      ...rest
    },
    ref,
  ) => {
    const cls = cn(
      BASE,
      SIZE_MAP[size],
      VARIANT_MAP[variant],
      glow && !disabled && 'shadow-[0_0_24px_var(--primary-glow)]',
      fullWidth && 'w-full',
      disabled ? 'opacity-60 pointer-events-none' : 'cursor-pointer',
      className,
    );

    if (asChild) {
      const slotProps: AnyProps = { className: cls, ref, ...rest };
      return <Slot {...slotProps}>{children}</Slot>;
    }

    return (
      <button className={cls} disabled={disabled} ref={ref} {...rest}>
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
